import React, { useState, useEffect } from 'react';
import authService from './authService';
import CleaningPeriodFilter from './CleaningPeriodFilter';
import { CleaningPeriod, defaultPeriod, filterJobsByPeriod, periodLabel } from './cleaningWeek';
import { buildPaymentSummary, exportPaymentsPDF, exportPaymentsWord } from './cleaningPayments';

// 📅 dd/mm/yyyy (μόνο εμφάνιση)
const fmtEU = (iso?: string): string => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || '');
};

// 🧽 Cleaning Jobs — ADMIN view (modal). Το backend φιλτράρει ΗΔΗ τα πεδία ανά ρόλο·
// εδώ απλώς προσαρμόζουμε τι δείχνουμε/επιτρέπουμε ανά ρόλο.
interface Props { onClose: () => void; }

const CleaningJobsAdmin: React.FC<Props> = ({ onClose }) => {
  const user = authService.getCurrentUser();
  const role = (user?.role || '').toUpperCase();
  const code = user?.code || '';

  const isAdmin = role === 'ADMIN';
  const isBooking = role === 'BOOKING';
  const isFinancial = role === 'FINANCIAL' || role === 'ACCOUNTING';
  const isTechnical = role === 'TECHNICAL';

  const canFullEdit = isAdmin || isBooking;            // instructions/deadline/amount/cleaned/remove/paid
  const canRemove = isAdmin || isBooking || isTechnical;
  const canTogglePaid = isAdmin || isBooking || isFinancial;
  const showAmount = isAdmin || isBooking || isFinancial;
  const showPaid = isAdmin || isBooking || isFinancial;
  const showTimes = isAdmin || isBooking || isTechnical;
  const showInstructions = isAdmin || isBooking || isTechnical;

  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [now] = useState(() => new Date());                        // 📅 σταθερό "τώρα"
  const [period, setPeriod] = useState<CleaningPeriod>(defaultPeriod); // default: τρέχουσα εβδομάδα
  const [view, setView] = useState<'jobs' | 'pay'>('jobs');        // 🧽 λίστα ή 💰 πληρωμές
  const [expanded, setExpanded] = useState<string | null>(null);   // drill-down ανά σκάφος
  const showPayments = isAdmin || isBooking || isFinancial;        // ποιοι βλέπουν το tab Πληρωμές

  const api = (opts: any = {}) => fetch('/api/cleaning-jobs.php' + (opts.qs || ''), {
    method: opts.method || 'GET',
    headers: { 'Content-Type': 'application/json', 'X-Employee-Code': code },
    ...(opts.body ? { body: JSON.stringify(opts.body) } : {})
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api(); const j = await r.json();
      setJobs(j.jobs || []);
    } catch (e) { setMsg('❌ Σφάλμα φόρτωσης'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const setLocal = (id: number, patch: any) =>
    setJobs(prev => prev.map(j => j.id === id ? { ...j, ...patch } : j));

  const saveField = async (id: number, patch: any) => {
    try { await api({ method: 'PUT', body: { id, ...patch } }); } catch (e) { setMsg('❌ Σφάλμα αποθήκευσης'); }
  };

  const togglePaid = async (job: any) => {
    const next = !job.paid;
    setLocal(job.id, { paid: next });
    try { await api({ qs: '?action=toggle-paid', method: 'POST', body: { id: job.id, paid: next } }); }
    catch (e) { setMsg('❌ Σφάλμα'); setLocal(job.id, { paid: job.paid }); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Αφαίρεση καθαρισμού;')) return;
    try { await api({ method: 'DELETE', body: { id } }); setJobs(prev => prev.filter(j => j.id !== id)); }
    catch (e) { setMsg('❌ Σφάλμα διαγραφής'); }
  };

  // 📅 φίλτρο περιόδου (default τρέχουσα εβδομάδα) — ισχύει σε λίστα + πληρωμές
  const filteredJobs = filterJobsByPeriod(jobs, period, now);
  const totalAmount = filteredJobs.reduce((s, j) => s + (parseFloat(j.amount) || 0), 0);
  const paySummary = buildPaymentSummary(filteredJobs);
  const periodTxt = periodLabel(period, now);

  // 💰 μαρκάρισμα όλων των απλήρωτων καθαρισμών ενός σκάφους ως paid
  const markVesselPaid = async (vesselJobs: any[]) => {
    const unpaid = vesselJobs.filter(j => !j.paid);
    if (unpaid.length === 0) return;
    if (!window.confirm(`Μαρκάρισμα ${unpaid.length} καθαρισμών ως πληρωμένων;`)) return;
    for (const j of unpaid) {
      setLocal(j.id, { paid: true });
      try { await api({ qs: '?action=toggle-paid', method: 'POST', body: { id: j.id, paid: true } }); }
      catch (e) { setMsg('❌ Σφάλμα'); setLocal(j.id, { paid: false }); }
    }
  };

  // 🏢 Στοιχεία Συνεργείου — ADMIN/BOOKING/FINANCIAL (ΟΧΙ TECHNICAL)
  const canCompany = isAdmin || isBooking || isFinancial;
  const COMPANY_FIELDS: [string, string][] = [
    ['company_name', 'Επωνυμία'], ['address', 'Διεύθυνση'], ['afm', 'ΑΦΜ'], ['doy', 'ΔΟΥ'],
    ['email', 'Email'], ['phone', 'Τηλέφωνο'], ['iban', 'IBAN']
  ];
  const [companies, setCompanies] = useState<any[]>([]);
  const loadCompanies = async () => {
    if (!canCompany) return;
    try {
      const j = await (await fetch('/api/cleaning-company.php', { headers: { 'X-Employee-Code': code } })).json();
      setCompanies(j.companies || []);
    } catch (e) {}
  };
  useEffect(() => { loadCompanies(); }, []); // eslint-disable-line
  const setCompanyLocal = (idx: number, key: string, val: string) =>
    setCompanies(prev => prev.map((c, i) => i === idx ? { ...c, [key]: val } : c));
  const saveCompany = async (c: any) => {
    try {
      await fetch('/api/cleaning-company.php', {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-Employee-Code': code },
        body: JSON.stringify({
          cleaning_user: c.cleaning_user,
          company_name: c.company_name || '', address: c.address || '', afm: c.afm || '',
          doy: c.doy || '', email: c.email || '', phone: c.phone || '', iban: c.iban || ''
        })
      });
      setMsg('✅ Στοιχεία συνεργείου αποθηκεύτηκαν'); setTimeout(() => setMsg(''), 2000);
    } catch (e) { setMsg('❌ Σφάλμα αποθήκευσης στοιχείων'); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-teal-600 flex items-center gap-2">
            <span>🧽</span><span>Καθαρισμοί</span>
            <span className="text-sm font-normal text-gray-400">({role})</span>
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl px-2">×</button>
        </div>

        {msg && <div className="px-4 py-2 text-sm text-red-600 bg-red-50">{msg}</div>}

        <div className="flex-grow overflow-y-auto p-4 space-y-3">
          {/* 🔀 view toggle (μόνο ρόλοι με πρόσβαση σε πληρωμές) + 📅 φίλτρο περιόδου */}
          {showPayments && (
            <div className="flex gap-2">
              <button onClick={() => setView('jobs')}
                className={`flex-1 py-2 rounded-lg font-bold text-sm ${view === 'jobs' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`}>🧽 Καθαρισμοί</button>
              <button onClick={() => setView('pay')}
                className={`flex-1 py-2 rounded-lg font-bold text-sm ${view === 'pay' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`}>💰 Πληρωμές</button>
            </div>
          )}
          <CleaningPeriodFilter period={period} now={now} onChange={setPeriod} />

          {view === 'pay' && showPayments ? (
            paySummary.rows.length === 0 ? (
              <div className="text-center text-gray-400 py-8">Δεν υπάρχουν καθαρισμοί σε αυτή την περίοδο.</div>
            ) : (
              <>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-white" style={{ background: '#1e90ff' }}>
                        <th className="text-left px-3 py-2">Σκάφος</th>
                        <th className="text-center px-2 py-2">Καθ.</th>
                        <th className="text-right px-2 py-2">Χρέωση</th>
                        <th className="text-right px-2 py-2">Πληρώθηκε</th>
                        <th className="text-right px-2 py-2">Υπόλοιπο</th>
                        {canTogglePaid && <th className="px-2 py-2"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {paySummary.rows.map(r => (
                        <React.Fragment key={r.vessel_name}>
                          <tr className="border-t border-gray-100 hover:bg-blue-50">
                            <td className="px-3 py-2 font-semibold text-blue-900 cursor-pointer"
                              onClick={() => setExpanded(expanded === r.vessel_name ? null : r.vessel_name)}>
                              <span className="text-gray-400 mr-1">{expanded === r.vessel_name ? '▾' : '▸'}</span>{r.vessel_name}
                            </td>
                            <td className="text-center px-2 py-2 text-gray-600">{r.count}</td>
                            <td className="text-right px-2 py-2">{r.charged.toFixed(2)} €</td>
                            <td className="text-right px-2 py-2 text-green-700">{r.paid.toFixed(2)} €</td>
                            <td className={`text-right px-2 py-2 font-bold ${r.balance === 0 ? 'text-green-600' : 'text-red-600'}`}>{r.balance.toFixed(2)} €</td>
                            {canTogglePaid && (
                              <td className="px-2 py-2 text-right">
                                {r.balance > 0 && (
                                  <button onClick={() => markVesselPaid(r.jobs)}
                                    className="px-2 py-1 rounded text-xs font-bold text-white bg-green-600 hover:bg-green-700 whitespace-nowrap">Πλήρωσα όλα</button>
                                )}
                              </td>
                            )}
                          </tr>
                          {expanded === r.vessel_name && r.jobs.map((j: any) => (
                            <tr key={j.id} className="bg-gray-50 text-xs text-gray-600">
                              <td className="px-3 py-1 pl-7">{fmtEU(j.cleaned_date)}{j.booking_number ? ` · ${j.booking_number}` : ''}</td>
                              <td></td>
                              <td className="text-right px-2 py-1">{(parseFloat(j.amount) || 0).toFixed(2)} €</td>
                              <td colSpan={canTogglePaid ? 1 : 2} className="text-right px-2 py-1">
                                <span className={`px-2 py-0.5 rounded font-bold ${j.paid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {j.paid ? '✅' : '⏳'}
                                </span>
                              </td>
                              {canTogglePaid && (
                                <td colSpan={2} className="text-right px-2 py-1">
                                  <button onClick={() => togglePaid(j)}
                                    className={`px-2 py-0.5 rounded text-xs font-bold text-white ${j.paid ? 'bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                                    {j.paid ? 'Ακύρωση' : 'Πληρώθηκε'}
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-blue-200 font-bold" style={{ background: '#e0f7ff' }}>
                        <td className="px-3 py-2 text-blue-900">ΣΥΝΟΛΟ</td>
                        <td className="text-center px-2 py-2">{paySummary.totals.count}</td>
                        <td className="text-right px-2 py-2">{paySummary.totals.charged.toFixed(2)} €</td>
                        <td className="text-right px-2 py-2 text-green-700">{paySummary.totals.paid.toFixed(2)} €</td>
                        <td className={`text-right px-2 py-2 ${paySummary.totals.balance === 0 ? 'text-green-600' : 'text-red-600'}`}>{paySummary.totals.balance.toFixed(2)} €</td>
                        {canTogglePaid && <td></td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => exportPaymentsPDF(paySummary, periodTxt)}
                    className="flex-1 py-2.5 rounded-lg font-bold text-white shadow" style={{ background: '#1e90ff' }}>📄 Export PDF</button>
                  <button onClick={() => exportPaymentsWord(paySummary, periodTxt)}
                    className="flex-1 py-2.5 rounded-lg font-bold text-white shadow bg-teal-600 hover:bg-teal-700">📝 Export Word</button>
                </div>
              </>
            )
          ) : (
          <>
          {/* 🏢 Στοιχεία Συνεργείου — ADMIN/BOOKING/FINANCIAL μόνο (ΟΧΙ TECHNICAL) */}
          {canCompany && (
            <div className="border border-blue-200 rounded-lg p-3 bg-blue-50/40">
              <div className="font-bold text-base mb-2" style={{ color: '#1e90ff' }}>🏢 Στοιχεία Συνεργείου</div>
              {companies.length === 0 ? (
                <div className="text-sm text-gray-500">Δεν έχουν καταχωρηθεί στοιχεία ακόμη (τα συμπληρώνει το συνεργείο).</div>
              ) : companies.map((c, idx) => (
                <div key={c.cleaning_user || idx} className="mb-3 p-2 bg-white rounded border border-gray-200">
                  <div className="text-xs text-gray-400 font-mono mb-1">{c.cleaning_user}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {COMPANY_FIELDS.map(([key, label]) => (
                      <label key={key} className="text-xs text-gray-500">{label}
                        <input type="text" value={c[key] || ''}
                          onChange={e => setCompanyLocal(idx, key, e.target.value)}
                          className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded text-sm" />
                      </label>
                    ))}
                  </div>
                  <button onClick={() => saveCompany(c)}
                    className="mt-2 px-4 py-2 rounded text-white text-sm font-bold"
                    style={{ background: 'linear-gradient(180deg, #6bb6f5 0%, #1e90ff 50%, #87ceeb 100%)' }}>
                    💾 Αποθήκευση
                  </button>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <div className="text-center text-gray-500 py-8">Φόρτωση…</div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center text-gray-400 py-8">Δεν υπάρχουν καθαρισμοί σε αυτή την περίοδο.</div>
          ) : filteredJobs.map(job => (
            <div key={job.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-grow">
                  <div className="font-bold text-gray-800">{job.vessel_name || '—'}</div>
                  {job.booking_number && <div className="text-xs text-gray-400 font-mono">{job.booking_number}</div>}
                </div>
                <div className="flex items-center gap-2">
                  {/* 🧽 cleaned status — visible to ΟΛΟΥΣ τους admin ρόλους (bug #3 fix) */}
                  <span className={`px-2 py-1 rounded text-xs font-bold ${job.cleaned ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {job.cleaned ? '✅ Καθαρίστηκε' : '🧽 Εκκρεμεί'}
                  </span>
                  {showPaid && (
                    <span className={`px-2 py-1 rounded text-xs font-bold ${job.paid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {job.paid ? '✅ Πληρώθηκε' : '⏳ Εκκρεμεί'}
                    </span>
                  )}
                  {canTogglePaid && (
                    <button onClick={() => togglePaid(job)}
                      className={`px-3 py-1 rounded text-xs font-bold text-white ${job.paid ? 'bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                      {job.paid ? 'Ακύρωση' : 'Πληρώθηκε'}
                    </button>
                  )}
                  {canRemove && (
                    <button onClick={() => remove(job.id)} className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-600 rounded text-sm" title="Αφαίρεση">✕</button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-sm">
                <label className="text-xs text-gray-500">Ημερομηνία
                  <input type="date" defaultValue={job.cleaned_date || ''} disabled={!canFullEdit}
                    onBlur={e => canFullEdit && saveField(job.id, { cleaned_date: e.target.value })}
                    className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded disabled:bg-gray-100" />
                </label>
                {showTimes && (
                  <>
                    <label className="text-xs text-gray-500">Ώρα έναρξης
                      <input type="time" defaultValue={job.start_time || ''}
                        onBlur={e => saveField(job.id, { start_time: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded" />
                    </label>
                    <label className="text-xs text-gray-500">Παραδόθηκε
                      <input type="time" defaultValue={job.end_time || ''}
                        onBlur={e => saveField(job.id, { end_time: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded" />
                    </label>
                  </>
                )}
                {showAmount && (
                  <label className="text-xs text-gray-500">Ποσό €
                    <input type="number" step="0.01" defaultValue={job.amount ?? ''} disabled={!canFullEdit}
                      onBlur={e => canFullEdit && saveField(job.id, { amount: e.target.value })}
                      className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded disabled:bg-gray-100" />
                  </label>
                )}
                {showTimes && (
                  <label className="text-xs text-gray-500">Να τελειώσει μέχρι
                    <input type="time" defaultValue={job.deadline_time || ''}
                      onBlur={e => saveField(job.id, { deadline_time: e.target.value })}
                      className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded" />
                  </label>
                )}
              </div>

              {showInstructions && (
                <label className="block text-xs text-gray-500 mt-2">Οδηγίες
                  <textarea defaultValue={job.admin_instructions || ''} disabled={!canFullEdit} rows={2}
                    onBlur={e => canFullEdit && saveField(job.id, { admin_instructions: e.target.value })}
                    className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded disabled:bg-gray-100" />
                </label>
              )}
            </div>
          ))}
          </>
          )}
        </div>

        {showAmount && view === 'jobs' && filteredJobs.length > 0 && (
          <div className="p-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
            <span className="font-bold text-gray-700">Σύνολο:</span>
            <span className="font-bold text-teal-600 text-lg">{totalAmount.toFixed(2)} €</span>
          </div>
        )}

        <div className="p-4 border-t border-gray-200">
          <button onClick={onClose} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-lg">Κλείσιμο</button>
        </div>
      </div>
    </div>
  );
};

export default CleaningJobsAdmin;
